/**
 * Sandbox CLI Hero
 *
 * The Canary-CLI-forward block shown both as the org-level empty state
 * (when no templates exist) and inside the help drawer once templates do
 * exist. Composes title + sub-copy + ConnectSteps + New Template CTA +
 * SandboxGuideAccordion.
 */
import React from 'react';
import { Button, Heading, Text } from '@chatsdet/ui';
import { Plus, Terminal } from '@phosphor-icons/react';
import { ConnectSteps } from './ConnectSteps';
import { SandboxGuideAccordion } from './SandboxGuideAccordion';
import { BRAND } from '../../../brand';

export interface SandboxCliHeroProps {
  /** Render the full-screen empty-state variant (large heading, no border).
   *  When false, renders the compact drawer variant. */
  variant: 'empty-state' | 'drawer';
  /** Whether to show the New Template CTA. The drawer variant skips it
   *  by default since templates already exist when the drawer is open. */
  onNewTemplate?: () => void;
  /** Disable the CTA — e.g., GitHub not connected. */
  isNewTemplateDisabled?: boolean;
}

export function SandboxCliHero({
  variant,
  onNewTemplate,
  isNewTemplateDisabled,
}: SandboxCliHeroProps) {
  const isEmpty = variant === 'empty-state';
  const { cliCommand: cli } = BRAND;

  return (
    <div
      className={
        isEmpty
          ? 'mx-auto max-w-2xl py-12 px-4 flex flex-col gap-8'
          : 'flex flex-col gap-6'
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <Terminal size={18} weight="fill" className="text-primary-foreground" />
          </div>
          <Heading level={isEmpty ? 2 : 4}>
            {isEmpty ? 'Spin up sandboxes from any repo' : `Connect with ${cli}-cli`}
          </Heading>
        </div>
        <Text color="muted">
          {isEmpty
            ? `Sandboxes are isolated, browser-reachable environments built from your GitHub repos. Drive them from the ${cli} CLI — author templates, launch instances, run actions, and tail logs from your terminal.`
            : `Drive any running sandbox from your terminal with the ${cli} CLI.`}
        </Text>
      </div>

      <div
        className={
          isEmpty
            ? 'rounded-lg border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-6'
            : ''
        }
      >
        <ConnectSteps />
      </div>

      {onNewTemplate && (
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            iconLeft={<Plus size={16} />}
            onPress={onNewTemplate}
            isDisabled={isNewTemplateDisabled}
          >
            New Template
          </Button>
          {isNewTemplateDisabled && (
            <Text size="sm" color="muted">
              Connect GitHub in Settings &gt; Integrations to create a template.
            </Text>
          )}
        </div>
      )}

      <SandboxGuideAccordion />
    </div>
  );
}

SandboxCliHero.displayName = 'SandboxCliHero';
